import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { match } from '@/utils/pattern-match-util'
import { DetailsViewContainer } from '@/features/jobs/details/components/DetailsViewContainer'
import { CommunicationViewContainer } from '@/features/jobs/details/components/CommunicationView/CommunicationViewContainer'
import { JobSummaryCardContainer } from '@/features/jobs/details/components/JobSummaryCard/JobSummaryCardContainer'
import { JobNotesCardContainer } from '@/features/jobs/details/components/JobNotesCard/JobNotesCardContainer'
import { CollaboratorsCardContainer } from '@/features/jobs/details/components/CollaboratorsCard/CollaboratorsCardContainer'
import {
  ContentView,
  SplitContentView,
} from '@/features/jobs/details/components/ContentView'
import { DragAndDropOverlayContainer } from '@/features/jobs/details/components/DragAndDropOverlay/DragAndDropOverlayContainer'
import { TasksCardContainer } from '@/features/jobs/details/components/TasksCard/TasksCardContainer'
import { JobProgressionCardContainer } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionCardContainers'
import { PaymentDistributionCardContainer } from '@/features/jobs/details/components/PaymentDistributionCard/PaymentDistributionCardContainer'
import { JobTicketsCardContainer } from '@/features/jobs/details/components/JobTicketsCard/JobTicketsCardContainer'
import { CompleteOnboardingCardContainer } from '@/features/jobs/details/components/CompleteOnboardingCard/CompleteOnboardingCardContainer'
import { JobInsightsCardContainer } from '@/features/jobs/details/components/JobInsightsCard/JobInsightsCardContainer'
import { InfoGatheringCardContainer } from '@/features/jobs/details/components/InfoGatheringCard/InfoGatheringCardContainer'
import {
  ConsultationCallCardContainer,
  DownloadCalendarButtonContainer,
} from '@/features/jobs/details/components/ConsultationCallCard/ConsultationCallCardContainer'
import { RatingsNotificationBarContainer } from '@/features/jobs/details/ratings/components/RatingsNotificationBar/RatingsNotificationBarContainer'
import { JobProgressionAndConsultationCardContainer } from '@/features/jobs/details/components/JobProgressionAndConsultationCardContainer'
import { BookkeepingProgressionCardContainer } from '@/features/jobs/details/components/BookkeepingProgressionCard/BookkeepingProgressionCardContainer'
import { BookkeepingTrackerCardContainer } from '@/features/jobs/details/components/BookkeepingTrackerCard/BookkeepingTrackerCardContainer'
import { RoomCardContainer } from '@/features/jobs/details/components/RoomCard/RoomCardContainer'

export interface ContentViewContainerProps {
  vm: JobDetailsViewModel
}

export const ContentViewContainer = observer(function ContentViewContainer({
  vm,
}: ContentViewContainerProps) {
  return (
    <ContentView>
      <RatingsNotificationBarContainer vm={vm.collaboratorsVm.ratingsVm} />
      <DragAndDropOverlayContainer
        vm={vm.documentsVm.uploadBarVm}
        targetJob={vm.job}
        onView={vm.currentTab === 'Details' ? undefined : vm.showDetailsTab}
      />

      <SplitContentViewContainer vm={vm} />
    </ContentView>
  )
})

const SplitContentViewContainer = observer(function SplitContentViewContainer({
  vm,
}: ContentViewContainerProps) {
  return (
    <SplitContentView
      leftSide={
        <>
          {match(vm.visibleCard, {
            CompleteOnboarding: () => (
              <CompleteOnboardingCardContainer vm={vm} />
            ),
            InfoGathering: () => <InfoGatheringCardContainer vm={vm} />,
            JobProgressionConsultation: () => (
              <JobProgressionAndConsultationCardContainer vm={vm} />
            ),
            ConsultationCall: () => <ConsultationCallCardContainer vm={vm} />,
            JobProgression: () => <JobProgressionCardContainer vm={vm} />,
            Task: () => <TasksCardContainer vm={vm} />,
            Bookkeeping: () => (
              <>
                <BookkeepingProgressionCardContainer
                  vm={vm.bookkeepingVm.bookkeepingProgressionVm}
                />
                <BookkeepingTrackerCardContainer
                  vm={vm.bookkeepingVm.bookkeepingProgressionVm}
                />
              </>
            ),
          })}

          {vm.consultationVm.showCallCard && (
            <RoomCardContainer
              vm={vm.consultationVm.roomVm}
              buttonGroup={
                <DownloadCalendarButtonContainer vm={vm.consultationVm} />
              }
            />
          )}

          {vm.canViewJobInsights && <JobInsightsCardContainer vm={vm} />}

          {vm.canViewJobNotes && <JobNotesCardContainer vm={vm} />}

          <CollaboratorsCardContainer vm={vm} />

          <JobTicketsCardContainer vm={vm} />

          <JobSummaryCardContainer vm={vm} />

          {vm.billingVm.canViewPaymentDistributionAsAdmin && (
            <PaymentDistributionCardContainer vm={vm} />
          )}
        </>
      }
      tab={match(vm.currentTab, {
        Details: () => <DetailsViewContainer vm={vm} />,
        Communication: () =>
          vm.showDraftView ? null : <CommunicationViewContainer vm={vm} />,
        Collaborators: () => null,
        Documents: () => null,
      })}
    />
  )
})
